import formatNumber from "@/app/infrastructures/misc/common-library/FormatNumber";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import jsPDF from "jspdf";
import { startCase } from "lodash";
import { codDfodLabel, estimationBound, normalizeDutyTaxTerms, showDutyTaxBadge } from "../../module";
import { createSolidBadge } from "./footer/utils";
import { FlagsFeatureShipmentC1 } from "@/feature-flags/flags-shipment-c1";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

const formatPrice = (value: number, country: string) => {
  return formatByCountry(value, {
    type: "money",
    country: country?.toUpperCase(),
    prefix: country === "MY" ? "RM" : "Rp"
  });
};

const formatWeight = (value: number, country: string) => {
  return formatByCountry(value, {
    type: "weight",
    country: country?.toUpperCase()
  });
};

const shiftX = (x: number) => x - 2;

const resolveFontStyle = (fontName: string, style: string) => {
  if (fontName === "Roboto Mono") {
    if (style === "bold") return "bold";
    if (style === "semibold") return "semibold";
    return "normal";
  }

  if (style === "extrabold") return "extrabold";
  if (style === "bold") return "bold";
  if (style === "semibold") return "semibold";
  return "medium";
};

const setFontAuto = (doc: jsPDF, text: string, style: string) => {
  const hasDigit = /[0-9]/.test(text);
  const fontName = hasDigit ? "Roboto Mono" : "Inter";
  doc.setFont(fontName, resolveFontStyle(fontName, style));
};

const computeBigpackFlags = (data: PrintDataEntitas) => {
  const product = String(data.sttProductType || "").toUpperCase();
  const cw = Number(data.sttChargeableWeight || 0);
  return {
    isBigpackVolume: cw <= 10 && product === "BIGPACK",
    isJumbopackvolume: cw <= 30 && product === "JUMBOPACK"
  };
};

const computeCwText = (
  data: PrintDataEntitas,
  flags: { isBigpackVolume: boolean; isJumbopackvolume: boolean }
) => {
  if (flags.isBigpackVolume) return "Min. 10 kg";
  if (flags.isJumbopackvolume) return "Min. 30 kg";
  return `${formatWeight(
    data.sttChargeableWeight,
    data.sttBookedByCountry
  )} kg`;
};

const computeTariff = (params: {
  bookingType: string;
  accountType: string;
  data: PrintDataEntitas;
}) => {
  let shipmentId = params.data.sttShipmentId ? params.data.sttShipmentId : "";
  let isPriceOfCOD = params.data.sttIsCod;
  let totalBiaya: any = 0;
  let totalBiayaKirim: any = 0;
  let lainLain: any = 0;
  let pajak: any = 0;
  let showPajak = true;
  let isEstimationBound = false;
  let returnFee: any = "-";
  let showReturnFee = false;

  const pajakValue =
    Number(params.data.sttBmTaxRate) +
    Number(params.data.sttPpnTaxRate) +
    Number(params.data.sttPphTaxRate);

  if (
    params.data.isReverseJourney &&
    (shipmentId.startsWith(
      FlagsFeatureShipmentC1.feature_shipment_c1.getValue()
    ) ||
      shipmentId.match(/^AP|^AS/gi))
  ) {
    shipmentId = params.data.sttReverseJourneyShipmentId;
    isPriceOfCOD = params.data.sttReverseJourneyCodHandling;
  }

  if (params.data.isReverseJourney) {
    shipmentId = params.data.sttReverseJourneyShipmentId;
  }

  isEstimationBound = estimationBound(
    shipmentId,
    params.bookingType,
    params.accountType,
    isPriceOfCOD
  );

  if (params.data.tariffVisible || !isEstimationBound) {
    totalBiaya = formatPrice(
      Number(params.data.totalTariff),
      params.data.sttBookedByCountry
    );
    const bKirimCalculation =
      Number(params.data.sttShippingSurchargeRateAfterDiscount) +
      Number(params.data.sttPublishRateAfterDiscount) +
      Number(params.data.sttOriginDistrictRateAfterDiscount) +
      Number(params.data.sttDestinationDistrictRateAfterDiscount);
    totalBiayaKirim = formatPrice(
      Number(bKirimCalculation),
      params.data.sttBookedByCountry
    );
    const lainLainCalculation = params.data.sttIsPromo
      ? Number(params.data.sttCommoditySurchargeRateAfterDiscount) +
        Number(params.data.sttHeavyweightSurchargeRateAfterDiscount) +
        Number(params.data.sttDocumentSurchargeRateAfterDiscount) +
        Number(params.data.sttInsuranceRate) +
        Number(params.data.sttCodFee) +
        Number(params.data.sttWoodpackingAfterRateDiscount)
      : Number(params.data.sttCommoditySurchargeRate) +
        Number(params.data.sttHeavyweightSurchargeRate) +
        Number(params.data.sttDocumentSurchargeRate) +
        Number(params.data.sttInsuranceRate) +
        Number(params.data.sttCodFee) +
        Number(params.data.sttWoodpackingRate);

    lainLain = formatPrice(lainLainCalculation, params.data.sttBookedByCountry);
    pajak = formatPrice(pajakValue, params.data.sttBookedByCountry);
    showPajak = pajakValue > 0;

    showReturnFee = params.data.sttIsCod === true;
    if (showReturnFee) {
      returnFee = formatPrice(
        Number(params.data.sttReturnFee),
        params.data.sttBookedByCountry
      );
    }
  } else {
    totalBiaya = "-";
    totalBiayaKirim = "-";
    lainLain = "-";
    if (pajakValue > 0) {
      pajak = "-";
    } else {
      showPajak = false;
    }
    showReturnFee = false;
  }

  if (params.data.sttIsCod) {
    totalBiaya = params.data.isInternational
      ? formatNumber(Number(params.data.sttCodAmount), {
          prefix: params.data.currencyCode,
          currency: params.data.currencyCode
        })
      : formatPrice(
          Number(params.data.sttCodAmount),
          params.data.sttBookedByCountry,
        );
  }

  return {
    totalBiaya,
    totalBiayaKirim,
    lainLain,
    pajak,
    showPajak,
    returnFee,
    showReturnFee,
    isEstimationBound
  };
};

export const rightPanelV3 = (
  params: {
    doc: jsPDF | any;
    bookingType: string;
    accountType: string;
    data: PrintDataEntitas;
    pieceData: any;
    startX: number;
    startY: number;
  },
  translate: Function
) => {
  const baseY = params.startY + 48;
  const xLine = shiftX(params.startX + 77);
  const xText = xLine + 1;

  const { isBigpackVolume, isJumbopackvolume } = computeBigpackFlags(
    params.data
  );

  const gwRaw =
    params.pieceData?.stt_piece_gross_weight ||
    params.pieceData?.[0]?.sttPieceGrossWeight ||
    params.pieceData?.sttPieceGrossWeight;
  const gwText =
    gwRaw !== undefined && gwRaw !== null && String(gwRaw) !== ""
      ? `${formatWeight(gwRaw, params.data.sttBookedByCountry)} kg`
      : "";

  params.doc.setTextColor("#333333");
  params.doc.setFontSize(15);
  setFontAuto(params.doc, gwText, "bold");
  params.doc.text(
    isBigpackVolume || isJumbopackvolume ? "-" : gwText,
    xText,
    baseY + 7
  );

  const l = Math.round(
    params.pieceData?.stt_piece_length ||
      params.pieceData?.[0]?.sttPieceLength ||
      params.pieceData?.sttPieceLength ||
      0
  );
  const w = Math.round(
    params.pieceData?.stt_piece_width ||
      params.pieceData?.[0]?.sttPieceWidth ||
      params.pieceData?.sttPieceWidth ||
      0
  );
  const h = Math.round(
    params.pieceData?.stt_piece_height ||
      params.pieceData?.[0]?.sttPieceHeight ||
      params.pieceData?.sttPieceHeight ||
      0
  );
  const vwText = `${l}x${w}x${h} cm`;

  params.doc.setFontSize(9);
  setFontAuto(params.doc, vwText, "semibold");
  params.doc.text(
    isBigpackVolume || isJumbopackvolume ? "-" : vwText,
    xText,
    baseY + 12
  );

  const cwLabel = "CW: ";
  const cwValue = computeCwText(params.data, {
    isBigpackVolume,
    isJumbopackvolume
  });
  params.doc.setFontSize(7);
  setFontAuto(params.doc, cwLabel, "bold");
  params.doc.text(cwLabel, xText, baseY + 15.5);
  setFontAuto(params.doc, cwValue, "semibold");
  params.doc.text(cwValue, xText + 6, baseY + 15.5);

  // line after gw, vw, cw
  params.doc.setLineWidth(0.4);
  params.doc.line(xLine, baseY + 18, shiftX(params.startX + 108), baseY + 18);

  const koliValue = String(
    params.pieceData?.stt_piece_count ||
      params.pieceData?.[0]?.sttPieceCount ||
      params.pieceData?.sttPieceCount ||
      ""
  );
  params.doc.setFontSize(26);
  setFontAuto(params.doc, koliValue, "bold");
  params.doc.text(koliValue, xText, baseY + 27.5);

  // line after pieces count
  const afterKoliY = baseY + 30;
  params.doc.setLineDash([], 0);
  params.doc.setLineWidth(0.4);
  params.doc.setDrawColor("#333333");
  params.doc.line(xLine, afterKoliY, shiftX(params.startX + 108), afterKoliY);

  const tariff = computeTariff(params);
  const isCodDfod =
    codDfodLabel(params.data.sttIsCod, params.data.sttIsDfod) === "cod" ||
    codDfodLabel(params.data.sttIsCod, params.data.sttIsDfod) === "dfod";

  if (isCodDfod) {
    const codDfodTitle =
      codDfodLabel(params.data.sttIsCod, params.data.sttIsDfod) === "cod"
        ? "COD"
        : "DFOD";
    params.doc.setFontSize(13);
    params.doc.setFont("Inter", "extrabold");
    params.doc.text(codDfodTitle, xText, baseY + 35);
    params.doc.setFontSize(8);
    setFontAuto(params.doc, String(tariff.totalBiaya), "bold");
    params.doc.text(String(tariff.totalBiaya), xText, baseY + 40);
  } else {
    params.doc.setFont("Inter", "bold");
    params.doc.setFontSize(6);
    params.doc.text(
      startCase(translate("receipt.totalCost")),
      xText,
      baseY + 35
    );
    params.doc.setFontSize(11);
    setFontAuto(params.doc, String(tariff.totalBiaya), "bold");
    params.doc.text(String(tariff.totalBiaya), xText, baseY + 39.5);
  }

  params.doc.setFont("Inter", "bold");
  params.doc.setFontSize(5);
  let rowY = baseY + 43;
  params.doc.text(translate("receipt.shippingCostV2"), xText, rowY);
  params.doc.text(":", xText + 13, rowY);
  setFontAuto(params.doc, String(tariff.totalBiayaKirim), "semibold");
  params.doc.text(String(tariff.totalBiayaKirim), xText + 14, rowY);

  params.doc.setFont("Inter", "bold");
  rowY += 3;
  params.doc.text(translate("receipt.othersV2"), xText, rowY);
  params.doc.text(":", xText + 13, rowY);
  setFontAuto(params.doc, String(tariff.lainLain), "semibold");
  params.doc.text(String(tariff.lainLain), xText + 14, rowY);

  if (!tariff.isEstimationBound && tariff.showReturnFee) {
    rowY += 3;
    params.doc.setTextColor("#333333");
    params.doc.setFont("Inter", "bold");
    params.doc.text(translate("receipt.returnFeeV3"), xText, rowY);
    params.doc.text(":", xText + 13, rowY);
    setFontAuto(params.doc, String(tariff.returnFee), "semibold");
    params.doc.text(String(tariff.returnFee), xText + 14, rowY);
    params.doc.setTextColor("#333333");
  }

  if (tariff.showPajak) {
    rowY += 3;
    params.doc.setTextColor("#333333");
    params.doc.setFont("Inter", "bold");
    params.doc.text(translate("receipt.estTax"), xText, rowY);
    params.doc.text(":", xText + 13, rowY);
    setFontAuto(params.doc, String(tariff.pajak), "semibold");
    params.doc.text(String(tariff.pajak), xText + 14, rowY);
    params.doc.setTextColor("#333333");
  }

  if (showDutyTaxBadge(params.data)) {
    const dutyTerms = normalizeDutyTaxTerms(params.data.sttDutyTaxTerms);
    if (dutyTerms) {
      rowY += 4.5;
      createSolidBadge(params.doc, dutyTerms, xText + 0.5, rowY, 7);
    }
  }
};
